/** @jsxImportSource @emotion/react */
import { Buttons, Dialogs, TextFields } from '@zip/business-components';
import { useFormik } from 'formik';
import moment from 'moment';
import { FC, useEffect } from 'react';
import { DateRange, DateRangeError } from 'types';
import { formikHasErrors } from 'utils/validators';
import { AddDateRangeDialogModuleProps } from './AddDateRangeDialogModuleProps';

const AddDateRangeFilterDialogModule: FC<AddDateRangeDialogModuleProps> = ({
  callback,
  onClose,
  open,
  fromDate,
  toDate,
}) => {
  const monthDiff = (d1, d2): number => {
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    return months <= 0 ? 0 : months;
  };

  const formik = useFormik({
    initialValues: {
      fromDate: fromDate,
      toDate: toDate,
    },
    onSubmit: () => handleSubmit(),
    validate: (values: DateRange) => {
      const errors: DateRangeError = {};
      if (!values.fromDate) {
        errors.fromDate = 'Required';
      }
      if (!values.toDate) {
        errors.toDate = 'Required';
      }
      if (monthDiff(new Date(values.fromDate), new Date(values.toDate)) > 3) {
        errors.toDate = 'Date range cannot exceed three months';
      }
      return errors;
    },
    validateOnMount: false,
  });

  const handleClose = (e?, r?): void => {
    onClose(e, r);
  };

  const handleSubmit = async (): Promise<void> => {
    callback(formik.values.fromDate, formik.values.toDate);
    handleClose();
  };

  useEffect(() => {
    formik.setFieldValue('fromDate', fromDate);
    formik.setFieldValue('toDate', toDate);
  }, [fromDate, toDate]);

  return (
    <Dialogs.Basic
      open={open}
      title="Date range"
      onClose={handleClose}
      spaceActions
      actions={
        <>
          <Buttons.Primary
            type="submit"
            onClick={(): void => formik.handleSubmit()}
            data-testid="save-button"
            disabled={!formikHasErrors(formik)}
          >
            Search
          </Buttons.Primary>

          <Buttons.Text onClick={handleClose}>Cancel</Buttons.Text>
        </>
      }
      mobileFullscreen
    >
      <form onSubmit={formik.handleSubmit}>
        <TextFields.Date
          slotProps={{
            openPickerButton: {
              'aria-label': 'open calendar',
            },
            textField: {
              id: 'fromDate',
              onBlur: formik.handleBlur,
              error: Boolean(formik.errors.fromDate),
              helperText: formik.errors.fromDate as string,
              fullWidth: true,
              placeholder: 'DD/MM/YYYY',
              'data-testid': 'fromDate',
            } as any,
          }}
          label="From"
          name="fromDate"
          onChange={(value): void => {
            // @material-ui/pickers sends the value to onChange, not the event object ಠ_ಠ
            formik.setFieldValue(
              'fromDate',
              value?.toDate ? value.toDate() : value
            );
          }}
          value={formik.values.fromDate ? moment(formik.values.fromDate) : null}
          disableFuture
          views={['year', 'month', 'day']}
        />
        <TextFields.Date
          slotProps={{
            openPickerButton: {
              'aria-label': 'open calendar',
            },
            textField: {
              id: 'toDate',
              onBlur: formik.handleBlur,
              error: Boolean(formik.errors.toDate),
              helperText: formik.errors.toDate as string,
              fullWidth: true,
              placeholder: 'DD/MM/YYYY',
              'data-testid': 'toDate',
            } as any,
          }}
          label="To"
          name="toDate"
          onChange={(value): void => {
            // @material-ui/pickers sends the value to onChange, not the event object ಠ_ಠ
            formik.setFieldValue(
              'toDate',
              value?.toDate ? value.toDate() : value
            );
          }}
          value={formik.values.toDate ? moment(formik.values.toDate) : null}
          disableFuture
          views={['year', 'month', 'day']}
        />
      </form>
    </Dialogs.Basic>
  );
};

export default AddDateRangeFilterDialogModule;
